import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../contexts/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import "./LastUpdatedBadge.css";

// time thresholds for stale warnings
const STALE_WARNING_MS = 24 * 60 * 60 * 1000; // 24 hours
const STALE_DANGER_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

const formatTimeAgo = (timestamp, t) => {
  if (!timestamp) return t("Never");

  const now = Date.now();
  const diff = now - timestamp;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 10) return t("Just now");
  if (seconds < 60) return t("{{count}} seconds ago", { count: seconds });
  if (minutes === 1) return t("1 minute ago");
  if (minutes < 60) return t("{{count}} minutes ago", { count: minutes });
  if (hours === 1) return t("1 hour ago");
  if (hours < 24) return t("{{count}} hours ago", { count: hours });
  if (days === 1) return t("1 day ago");
  return t("{{count}} days ago", { count: days });
};

// get staleness level based on timestamp
const getStalenessLevel = (timestamp) => {
  if (!timestamp) return "normal";
  
  const age = Date.now() - timestamp;
  
  if (age >= STALE_DANGER_MS) return "danger";
  if (age >= STALE_WARNING_MS) return "warning";
  return "normal";
};

function LastUpdatedBadge({ timestamp, onRefresh, isRefreshing = false, variant = "default" }) {
  const { t } = useTranslation();
  const { darkMode } = useDarkMode();
  const [, setTick] = useState(0);
  const [justRefreshed, setJustRefreshed] = useState(false);
  const prevTimestampRef = useRef(timestamp);

  // re-render every 30 seconds to update the "ago" text
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (timestamp && prevTimestampRef.current && timestamp !== prevTimestampRef.current) {
      setJustRefreshed(true);
      const timer = setTimeout(() => setJustRefreshed(false), 600);
      return () => clearTimeout(timer);
    }
    prevTimestampRef.current = timestamp;
  }, [timestamp]);

  const timeAgo = formatTimeAgo(timestamp, t);
  const stalenessLevel = getStalenessLevel(timestamp);

  const handleRefreshClick = () => {
    if (onRefresh && !isRefreshing) {
      onRefresh();
    }
  };

  const badgeClasses = [
    "last-updated-badge",
    darkMode ? "dark" : "light",
    `staleness-${stalenessLevel}`,
    variant !== "default" ? `variant-${variant}` : "",
    justRefreshed ? "just-refreshed" : "",
  ].filter(Boolean).join(" ");

  // tooltip for stale data
  const getTooltip = () => {
    if (stalenessLevel === "danger") {
      return t("Data is over 3 days old. Consider refreshing.");
    }
    if (stalenessLevel === "warning") {
      return t("Data is over 24 hours old.");
    }
    return "";
  };

  return (
    <div className={badgeClasses} title={getTooltip()}>
      <span className="last-updated-text">
        {t("Last updated")}: {timeAgo}
      </span>
      {onRefresh && (
        <button
          className={`refresh-button ${isRefreshing ? "spinning" : ""}`}
          onClick={handleRefreshClick}
          disabled={isRefreshing}
          title={t("Refresh data")}
        >
          <FontAwesomeIcon icon={faSync} />
        </button>
      )}
    </div>
  );
}

export default LastUpdatedBadge;
